/**
 * Typed HTTP client for the game Worker (routes listed at the top of src/shared/protocol.ts).
 * Every call returns the parsed JSON body or throws `ApiRequestError` with plain words to show.
 * Tokens travel only in the Authorization header, never in URLs.
 */
import {
  PROTOCOL_VERSION,
  type CreateWorldRequest,
  type CreateWorldResponse,
  type EnvelopesResponse,
  type ErrorBody,
  type ErrorCode,
  type JoinLookupResponse,
  type OkResponse,
  type OptOutRequest,
  type PinLoginResponse,
  type PlacementPublishRequest,
  type ProjectorTokenResponse,
  type QuizKeyResponse,
  type QuizUploadRequest,
  type RosterResponse,
  type TeacherLoginRequest,
  type TeacherLoginResponse,
  type TeacherRotateResponse,
} from '@shared/protocol.ts';
import type { ActionError, ClientView, StudentId } from '@shared/types.ts';

export type ClientErrorCode = ErrorCode | 'network' | 'timeout';

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ClientErrorCode;
  readonly retryAfterMs: number | undefined;
  readonly detail: ActionError | undefined;

  constructor(status: number, code: ClientErrorCode, message: string, retryAfterMs?: number, detail?: ActionError) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.retryAfterMs = retryAfterMs;
    this.detail = detail;
  }
}

const TIMEOUT_MS = 15_000;

const FALLBACK: Partial<Record<number, string>> = {
  400: 'That request did not look right. Check what you typed and try again.',
  401: 'Please sign in again.',
  403: 'You are not allowed to do that.',
  404: 'We could not find that. Check the code and try again.',
  409: 'Someone else changed this at the same time. Try again.',
  413: 'That is too much to send at once.',
  429: 'Too many tries. Wait a moment and try again.',
};

function isErrorBody(v: unknown): v is ErrorBody {
  return typeof v === 'object' && v !== null && typeof (v as { error?: unknown }).error === 'string' && typeof (v as { message?: unknown }).message === 'string';
}

async function request<T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, opts: { body?: unknown; token?: string | null } = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (opts.token) headers.authorization = `Bearer ${opts.token}`;
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), TIMEOUT_MS) : null;
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: ctrl?.signal,
      credentials: 'same-origin',
      cache: 'no-store',
    });
  } catch (err) {
    const aborted = err instanceof DOMException && err.name === 'AbortError';
    throw new ApiRequestError(
      0,
      aborted ? 'timeout' : 'network',
      aborted ? 'The server took too long to answer. Try again.' : 'We could not reach the server. Check the Wi-Fi and try again.',
    );
  } finally {
    if (timer) clearTimeout(timer);
  }
  let data: unknown = null;
  const text = await res.text().catch(() => '');
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    if (isErrorBody(data)) throw new ApiRequestError(res.status, data.error, data.message, data.retryAfterMs, data.detail);
    const retry = Number(res.headers.get('retry-after'));
    throw new ApiRequestError(
      res.status,
      res.status === 401 ? 'unauthorized' : res.status === 404 ? 'not_found' : res.status === 429 ? 'rate_limited' : 'internal',
      FALLBACK[res.status] ?? 'Something went wrong on the server. Try again in a moment.',
      Number.isFinite(retry) && retry > 0 ? retry * 1000 : undefined,
    );
  }
  return data as T;
}

const w = (id: string) => `/worlds/${encodeURIComponent(id)}`;

export const api = {
  health: () => request<{ ok: true }>('GET', '/health'),

  createWorld: (body: Omit<CreateWorldRequest, 'v'>) => request<CreateWorldResponse>('POST', '/worlds', { body: { ...body, v: PROTOCOL_VERSION } }),

  joinLookup: (classCode: string) => request<JoinLookupResponse>('POST', '/join', { body: { classCode } }),

  pinLogin: (worldId: string, studentId: StudentId, pin: string) =>
    request<PinLoginResponse>('POST', `${w(worldId)}/login`, { body: { studentId, pin } }),

  teacherLogin: (worldId: string, body: TeacherLoginRequest) => request<TeacherLoginResponse>('POST', `${w(worldId)}/teacher/login`, { body }),

  putRoster: (worldId: string, token: string, names: string[]) => request<RosterResponse>('PUT', `${w(worldId)}/roster`, { token, body: { names } }),

  getRoster: (worldId: string, token: string) => request<RosterResponse>('GET', `${w(worldId)}/roster`, { token }),

  quizKey: (worldId: string, token: string) => request<QuizKeyResponse>('GET', `${w(worldId)}/quiz/key`, { token }),

  uploadQuiz: (worldId: string, token: string, body: QuizUploadRequest) => request<OkResponse>('POST', `${w(worldId)}/quiz`, { token, body }),

  envelopes: (worldId: string, token: string) => request<EnvelopesResponse>('GET', `${w(worldId)}/quiz/envelopes`, { token }),

  optOut: (worldId: string, token: string, body: OptOutRequest) => request<OkResponse>('POST', `${w(worldId)}/quiz/optout`, { token, body }),

  publishPlacement: (worldId: string, token: string, body: PlacementPublishRequest) =>
    request<OkResponse>('POST', `${w(worldId)}/placement`, { token, body }),

  view: (worldId: string, token: string) => request<ClientView>('GET', `${w(worldId)}/view`, { token }),

  /** Lifts a student's PIN lock (including the daily cap). */
  unlockStudent: (worldId: string, token: string, studentId: StudentId) =>
    request<RosterResponse>('POST', `${w(worldId)}/students/unlock`, { token, body: { studentId } }),

  /** Gives a student a new PIN and signs out every device that used the old one. */
  resetStudent: (worldId: string, token: string, studentId: StudentId) =>
    request<RosterResponse>('POST', `${w(worldId)}/students/reset`, { token, body: { studentId } }),

  /** A new teacher key and recovery code; every other teacher and projector sign-in ends. */
  rotateTeacher: (worldId: string, token: string) => request<TeacherRotateResponse>('POST', `${w(worldId)}/teacher/rotate`, { token, body: {} }),

  /** A read-only sign-in for the projector screen. */
  projectorToken: (worldId: string, token: string) => request<ProjectorTokenResponse>('POST', `${w(worldId)}/teacher/projector`, { token, body: {} }),

  /** Deletes the whole world (names, chat, journals, everything). Cannot be undone. */
  deleteWorld: (worldId: string, token: string) => request<OkResponse>('DELETE', `${w(worldId)}/world`, { token }),
};

/** Plain words for any thrown value. */
export function errorText(err: unknown): string {
  if (err instanceof ApiRequestError) {
    if (err.code === 'pin_locked' && err.retryAfterMs) {
      const min = Math.max(1, Math.ceil(err.retryAfterMs / 60_000));
      return `${err.message} Try again in about ${min} minute${min === 1 ? '' : 's'}.`;
    }
    return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Something went wrong. Try again.';
}
